/**
 * Central utility functions for issue management
 */

import { buildNetlifyCallback } from "@/lib/backend";
import type {
  DatabaseInsertSingleRequest,
  DatabaseSelectRequest,
  DatabaseUpdateRequest,
} from "@/types/database";

const doDatabaseSelect = buildNetlifyCallback<DatabaseSelectRequest<any>, any[]>("DatabaseSelect");
const doDatabaseUpdate = buildNetlifyCallback<DatabaseUpdateRequest<any>, any>("DatabaseUpdate");
const doDatabaseInsertSingle = buildNetlifyCallback<DatabaseInsertSingleRequest<any>, any>(
  "DatabaseInsertSingle"
);

/**
 * Request type for updating issue timestamp and participation
 */
export interface UpdateIssueTimestampAndParticipationRequest {
  issueId: string;
  userId: string;
  participationType: "commented" | "mentioned" | "assigned" | "created";
}

/**
 * Updates the issue's `updated_at` timestamp and ensures the user is added
 * as a participant if not already present. This centralized function ensures
 * consistency regardless of where modifications are initiated.
 *
 * @param issueId The ID of the issue to update
 * @param userId The ID of the user performing the action
 * @param participationType The type of participation (defaults to 'commented')
 */
export async function updateIssueTimestampAndParticipation(
  issueId: string,
  userId: string,
  participationType: "commented" | "mentioned" | "assigned" | "created" = "commented"
): Promise<void> {
  try {
    // Update issue's updated_at timestamp
    await doDatabaseUpdate({
      table: "issues",
      filter: {
        type: "eq",
        column: "id",
        value: issueId,
      },
      data: {
        updated_at: new Date().toISOString(),
      },
    });

    // Check if user is already a participant
    const existingParticipantsResult = (await doDatabaseSelect({
      table: "issue_participants",
      filters: [
        { type: "eq", column: "issue_id", value: issueId },
        { type: "eq", column: "user_id", value: userId },
      ],
    })) as { data?: any[]; error?: any };

    const existingParticipants = existingParticipantsResult.data || [];

    // Add user as participant if not already present
    if (existingParticipants.length === 0) {
      await doDatabaseInsertSingle({
        table: "issue_participants",
        data: {
          issue_id: issueId,
          user_id: userId,
          participation_type: participationType,
        },
      });
    }
  } catch (error) {
    console.error("Failed to update issue timestamp and participation:", error);
    // Don't throw - this is a best-effort update that shouldn't block the main operation
  }
}

/**
 * Updates the last_opened_at timestamp for a user's participation record on an issue.
 * If no participation record exists, creates one with type 'viewed'.
 * This should be called whenever a user opens/views an issue.
 *
 * @param issueId The ID of the issue being opened
 * @param userId The ID of the user opening the issue
 */
export async function updateIssueLastOpened(issueId: string, userId: string): Promise<void> {
  try {
    const now = new Date().toISOString();

    // Check if user already has a participation record for this issue
    const existingParticipantsResult = (await doDatabaseSelect({
      table: "issue_participants",
      filters: [
        { type: "eq", column: "issue_id", value: issueId },
        { type: "eq", column: "user_id", value: userId },
      ],
    })) as { data?: any[]; error?: any };

    const existingParticipants = existingParticipantsResult.data || [];

    if (existingParticipants.length > 0) {
      // Update existing participation record with last_opened_at
      await doDatabaseUpdate({
        table: "issue_participants",
        filter: {
          type: "eq",
          column: "id",
          value: existingParticipants[0].id,
        },
        data: {
          last_opened_at: now,
        },
      });
    } else {
      // Create new participation record with type 'viewed' and last_opened_at
      await doDatabaseInsertSingle({
        table: "issue_participants",
        data: {
          issue_id: issueId,
          user_id: userId,
          participation_type: "viewed",
          last_opened_at: now,
        },
      });
    }
  } catch (error) {
    console.error("Failed to update issue last_opened_at:", error);
    // Don't throw - this is a best-effort update that shouldn't block the main operation
  }
}

/**
 * Generate the next issue number for a new issue
 * @returns Promise<string> The next issue number in format "ISS-XXX"
 */
export async function generateNextIssueNumber(): Promise<string> {
  const existingIssuesResult = (await doDatabaseSelect({
    table: "issues",
    orderColumn: "created_at",
    orderOptions: { ascending: false },
    limit: 1,
  })) as { data?: any[]; error?: any };

  const existingIssues = existingIssuesResult.data || [];

  const nextNumber =
    existingIssues && existingIssues.length > 0
      ? Number.parseInt(existingIssues[0].issue_number.split("-")[1]) + 1
      : 1;

  return `ISS-${String(nextNumber).padStart(3, "0")}`;
}

/**
 * Get the URL path for an issue
 * Prefers issue_number if available, falls back to issue.id
 * @param issueIdOrNumber Either issue ID (UUID) or issue number (ISS-XXX)
 * @returns string The URL path for the issue
 */
export function getIssueUrl(issueIdOrNumber: string): string {
  return `/issue/${issueIdOrNumber}`;
}

/**
 * Navigate to an issue using react-router's navigate function
 * @param navigate The navigate function from useNavigate()
 * @param issueIdOrNumber Either issue ID (UUID) or issue number (ISS-XXX)
 */
export function navigateToIssue(navigate: (path: string) => void, issueIdOrNumber: string): void {
  navigate(getIssueUrl(issueIdOrNumber));
}
